/**
 * CSV Export Utility
 * Handles CSV generation and download for all data types
 * Supports large datasets (10,000+ rows) without browser freeze
 */

import Papa from 'papaparse';

export interface ExportColumn {
  key: string;
  label: string;
  transform?: (value: unknown, row?: Record<string, unknown>) => string;
}

/**
 * Generate a filename with carrier name and timestamp
 */
export const generateFilename = (
  prefix: string,
  carrierName?: string,
  extension: string = 'csv'
): string => {
  const sanitizedName = carrierName
    ? carrierName.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30)
    : 'export';
  const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
  return `${prefix}_${sanitizedName}_${timestamp}.${extension}`;
};

// Generic type for data that can be exported - uses index signature for compatibility
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ExportableRecord = { [key: string]: any };

/**
 * Generic CSV export function
 * Handles large datasets by processing in chunks
 */
export const exportToCSV = async (
  data: ExportableRecord[],
  filename: string,
  columns?: ExportColumn[],
  onProgress?: (progress: number) => void
): Promise<void> => {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }

  // For large datasets, process in chunks
  const CHUNK_SIZE = 5000;
  let transformedData: Record<string, unknown>[] = [];

  if (columns) {
    // Process data in chunks to avoid blocking UI
    for (let i = 0; i < data.length; i += CHUNK_SIZE) {
      const chunk = data.slice(i, i + CHUNK_SIZE);
      const transformedChunk = chunk.map(row => {
        const transformed: Record<string, string> = {};
        columns.forEach(col => {
          const value = getNestedValue(row, col.key);
          transformed[col.label] = col.transform
            ? col.transform(value, row)
            : formatValue(value);
        });
        return transformed;
      });
      transformedData = transformedData.concat(transformedChunk);

      if (onProgress) {
        onProgress(Math.min(100, Math.round((i + CHUNK_SIZE) / data.length * 100)));
      }

      // Yield to UI thread for large datasets
      if (data.length > CHUNK_SIZE) {
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }
  } else {
    transformedData = data;
  }

  const csv = Papa.unparse(transformedData, {
    quotes: true,
    skipEmptyLines: true,
  });

  downloadFile(csv, filename, 'text/csv;charset=utf-8;');
};

/**
 * Get nested object value using dot notation
 */
const getNestedValue = (obj: Record<string, unknown>, path: string): unknown => {
  return path.split('.').reduce((current: unknown, key: string) => {
    return (current as Record<string, unknown>)?.[key];
  }, obj);
};

/**
 * Format value for CSV output
 */
const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

/**
 * Download file in browser
 */
const downloadFile = (content: string, filename: string, mimeType: string): void => {
  const blob = new Blob([content], { type: mimeType });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

// ==========================================
// SPECIALIZED EXPORT FUNCTIONS
// ==========================================

// Generic type for data that can be exported - allows typed arrays
type ExportableData = Record<string, unknown> | { [key: string]: unknown };

/**
 * Export violations to CSV
 */
export const exportViolationsToCSV = (
  violations: ExportableData[],
  carrierName?: string,
  onProgress?: (progress: number) => void
): Promise<void> => {
  const columns: ExportColumn[] = [
    { key: 'inspectionDate', label: 'Inspection Date', transform: formatDate },
    { key: 'violationCode', label: 'Violation Code' },
    { key: 'description', label: 'Description' },
    { key: 'basicCategory', label: 'BASIC Category' },
    { key: 'severity', label: 'Severity' },
    { key: 'outOfServiceIndicator', label: 'Out of Service', transform: (v) => v === 'Y' ? 'Yes' : 'No' },
    { key: 'inspectionState', label: 'State' },
    { key: 'vin', label: 'VIN' },
    { key: 'unitMake', label: 'Unit Make' },
    { key: 'unitType', label: 'Unit Type', transform: formatUnitType },
    { key: 'inspViolUnit', label: 'Violation Unit' },
    { key: 'inspectionId', label: 'Inspection ID' },
    { key: 'inspViolationId', label: 'Violation ID' },
  ];

  const filename = generateFilename('violations', carrierName);
  return exportToCSV(violations, filename, columns, onProgress);
};

/**
 * Export inspections to CSV
 */
export const exportInspectionsToCSV = (
  inspections: ExportableData[],
  carrierName?: string,
  onProgress?: (progress: number) => void
): Promise<void> => {
  const columns: ExportColumn[] = [
    { key: 'insp_date', label: 'Inspection Date', transform: formatDate },
    { key: 'inspection_id', label: 'Inspection ID' },
    { key: 'reportState', label: 'State' },
    { key: 'inspLevelId', label: 'Inspection Level' },
    { key: 'locationDesc', label: 'Location' },
    { key: 'violTotal', label: 'Total Violations' },
    { key: 'oosTotal', label: 'Out of Service Count' },
    { key: 'shipperName', label: 'Shipper Name' },
    { key: 'primaryVin', label: 'Primary VIN' },
    { key: 'hazmatInsp', label: 'Hazmat Inspection', transform: (v) => v === 'Y' ? 'Yes' : 'No' },
    { key: 'placarableHazmat', label: 'Placardable Hazmat', transform: (v) => v === 'Y' ? 'Yes' : 'No' },
  ];

  const filename = generateFilename('inspections', carrierName);
  return exportToCSV(inspections, filename, columns, onProgress);
};

/**
 * Export crashes to CSV
 */
export const exportCrashesToCSV = (
  crashes: ExportableData[],
  carrierName?: string,
  onProgress?: (progress: number) => void
): Promise<void> => {
  const columns: ExportColumn[] = [
    { key: 'reportDate', label: 'Report Date', transform: formatDate },
    { key: 'crashId', label: 'Crash ID' },
    { key: 'reportState', label: 'State' },
    { key: 'city', label: 'City' },
    { key: 'location', label: 'Location' },
    { key: 'vehicleIdentificationNumber', label: 'VIN' },
    { key: 'fatalities', label: 'Fatalities' },
    { key: 'injuries', label: 'Injuries' },
    { key: 'towAway', label: 'Tow Away', transform: (v) => v === 'Y' ? 'Yes' : 'No' },
    { key: 'hazmatReleased', label: 'Hazmat Released', transform: (v) => v === 'Y' ? 'Yes' : 'No' },
    { key: 'severity', label: 'Severity', transform: (v, row) => {
      if (Number(row?.fatalities || 0) > 0) return 'Fatal';
      if (Number(row?.injuries || 0) > 0) return 'Injury';
      if (row?.towAway === 'Y') return 'Tow-Away';
      return 'Property Damage';
    }},
  ];

  const filename = generateFilename('crashes', carrierName);
  return exportToCSV(crashes, filename, columns, onProgress);
};

/**
 * Export BASIC scores to CSV
 */
export const exportBASICScoresToCSV = (
  basics: ExportableData[],
  carrierName?: string,
  onProgress?: (progress: number) => void
): Promise<void> => {
  const columns: ExportColumn[] = [
    {
      key: 'basic.basicsType.basicsShortDesc',
      label: 'BASIC Category'
    },
    {
      key: 'basic.basicsPercentile',
      label: 'Percentile Score'
    },
    {
      key: 'basic.basicsViolationThreshold',
      label: 'Violation Threshold'
    },
    {
      key: 'basic.exceededFMCSAInterventionThreshold',
      label: 'Exceeded Threshold',
      transform: (v) => v === 'Y' ? 'Yes' : 'No'
    },
    {
      key: 'basic.totalInspectionWithViolation',
      label: 'Inspections with Violations'
    },
    {
      key: 'basic.totalViolation',
      label: 'Total Violations'
    },
    {
      key: 'basic.basicsRunDate',
      label: 'Last Updated',
      transform: formatDate
    },
    {
      key: 'basic.estimated',
      label: 'Estimated',
      transform: (v) => v ? 'Yes' : 'No'
    },
    {
      key: 'basic.riskLevel',
      label: 'Risk Level'
    },
  ];

  const filename = generateFilename('basic_scores', carrierName);
  return exportToCSV(basics, filename, columns, onProgress);
};

/**
 * Export carrier profile to CSV
 */
export const exportCarrierProfileToCSV = (
  profile: ExportableData,
  carrierName?: string
): Promise<void> => {
  const profileData = [{
    'DOT Number': profile.dotNumber,
    'Legal Name': profile.legalName,
    'DBA Name': profile.dbaName || '',
    'Status': profile.statusCode,
    'Allowed to Operate': profile.allowedToOperate === 'Y' ? 'Yes' : 'No',
    'Total Drivers': profile.totalDrivers,
    'Total Power Units': profile.totalPowerUnits,
    'Safety Rating': profile.safetyRating || 'Unrated',
    'Safety Rating Date': formatDate(profile.safetyRatingDate),
    'Total Crashes': profile.crashTotal,
    'Fatal Crashes': profile.fatalCrash,
    'Injury Crashes': profile.injCrash,
    'Tow-Away Crashes': profile.towawayCrash,
    'Driver Inspections': profile.driverInsp,
    'Driver OOS Inspections': profile.driverOosInsp,
    'Driver OOS Rate (%)': profile.driverOosRate,
    'Vehicle Inspections': profile.vehicleInsp,
    'Vehicle OOS Inspections': profile.vehicleOosInsp,
    'Vehicle OOS Rate (%)': profile.vehicleOosRate,
    'Physical Address': `${profile.phyStreet}, ${profile.phyCity}, ${profile.phyState} ${profile.phyZipcode}`,
    'Phone': profile.phone || '',
    'Email': profile.email || '',
    'EIN': profile.ein || '',
    'Common Authority': profile.commonAuthorityStatus || '',
    'Contract Authority': profile.contractAuthorityStatus || '',
    'Broker Authority': profile.brokerAuthorityStatus || '',
    'BIPD Insurance on File': profile.bipdInsuranceOnFile || '',
    'Cargo Insurance on File': profile.cargoInsuranceOnFile || '',
  }];

  const filename = generateFilename('carrier_profile', carrierName);
  return exportToCSV(profileData, filename);
};

/**
 * Export fleet vehicles to CSV
 */
export const exportFleetToCSV = (
  vehicles: ExportableData[],
  carrierName?: string,
  onProgress?: (progress: number) => void
): Promise<void> => {
  const columns: ExportColumn[] = [
    { key: 'vin', label: 'VIN' },
    { key: 'vinStatus', label: 'VIN Status' },
    { key: 'make', label: 'Make' },
    { key: 'model', label: 'Model' },
    { key: 'year', label: 'Year' },
    { key: 'unitType', label: 'Unit Type', transform: formatUnitType },
    { key: 'inspectionCount', label: 'Inspection Count' },
    { key: 'violationCount', label: 'Violation Count' },
    { key: 'crashCount', label: 'Crash Count' },
    { key: 'firstSeen', label: 'First Seen', transform: formatDate },
    { key: 'lastSeen', label: 'Last Seen', transform: formatDate },
    { key: 'multiCarrier', label: 'Multi-Carrier', transform: (v) => v ? 'Yes' : 'No' },
    { key: 'carrierCount', label: 'Total Carriers' },
  ];

  const filename = generateFilename('fleet_vehicles', carrierName);
  return exportToCSV(vehicles, filename, columns, onProgress);
};

// ==========================================
// HELPER FUNCTIONS
// ==========================================

/**
 * Format date for export
 */
const formatDate = (value: unknown): string => {
  if (!value) return '';
  try {
    const date = new Date(value as string | number | Date);
    if (isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  } catch {
    return String(value);
  }
};

/**
 * Format unit type for export
 */
const formatUnitType = (value: unknown): string => {
  const unitTypeMap: Record<number, string> = {
    9: 'Trailer',
    11: 'Truck/Tractor',
  };
  return unitTypeMap[value as number] || String(value || '');
};
